import { useState } from "react";
import { api, formatApiError, fmtCurrency, fmtInt } from "@/lib/api";
import { useT } from "@/lib/i18n";
import { useAuth } from "@/contexts/AuthContext";
import { useTheme } from "@/contexts/ThemeContext";
import { toast } from "sonner";
import { Sparkles, Lock, Sliders } from "lucide-react";
import HelpButton from "@/components/HelpButton";
import {
  ResponsiveContainer, BarChart, Bar, XAxis, YAxis, Tooltip, CartesianGrid, ReferenceLine, Cell,
} from "recharts";

// Human-readable labels per variable key. Values are i18n keys → useT() will translate.
const LABEL_KEYS = {
  "rate:inflation_rate": "Inflatie (±1pp)",
  "rate:discount_rate": "Disconteringsvoet (±1pp)",
  "rate:revenue_growth_rate": "Groei omzet (±1pp)",
  "rate:opex_growth_rate": "Groei kosten (±1pp)",
  "rate:tax_rate": "Belastingtarief (±1pp)",
};

/**
 * Deterministic one-variable-at-a-time sensitivity analysis (a.k.a. tornado).
 * Tier-gated to TRIAL/PREMIUM (mirrors MonteCarloPanel). Uses POST /api/scenarios/{sid}/sensitivity.
 */
export default function SensitivityPanel({ sid, scenario }) {
  const t = useT();
  const { theme } = useTheme();
  const { user } = useAuth();
  const isPremium = user && (user.tier === "TRIAL" || user.tier === "PREMIUM" || user.role === "admin");
  const [step, setStep] = useState(0.2);
  const [loading, setLoading] = useState(false);
  const [result, setResult] = useState(null);

  const run = async () => {
    setLoading(true);
    try {
      const { data } = await api.post(`/scenarios/${sid}/sensitivity`, { step });
      setResult(data);
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail) || t("Sensitivity-berekening mislukt"));
    } finally {
      setLoading(false);
    }
  };

  const labelFor = (label) => {
    if (LABEL_KEYS[label]) return t(LABEL_KEYS[label]);
    if (label?.startsWith("invplan.")) {
      const idx = parseInt(label.split(".")[1] || "0", 10);
      const name = scenario?.scenario_future_investments?.[idx]?.name || t("Investering {n}").replace("{n}", String(idx + 1));
      return `${name} — ${t("bedrag")} (±${Math.round(step * 100)}%)`;
    }
    return label;
  };

  // Theme-aware chart tokens so the tornado is legible in light + dark.
  const isDark = theme === "dark";
  const gridColor = isDark ? "hsl(220 15% 25%)" : "#e2e8f0";
  const zeroColor = isDark ? "hsl(210 30% 96%)" : "#0A0A0A";
  const axisColor = isDark ? "hsl(210 30% 80%)" : "#374151";

  return (
    <div className="mt-8 bg-card border-2 border-foreground heavy-shadow" data-testid="sensitivity-panel">
      <header className="p-5 border-b-2 border-foreground flex items-center justify-between gap-3 flex-wrap">
        <div>
          <div className="stat-label inline-flex items-center gap-2">
            <Sliders className="w-3 h-3" /> {t("Gevoeligheidsanalyse (tornado)")}
          </div>
          <h2 className="font-display font-black text-2xl uppercase tracking-tight">{t("Welke aannames raken de netto contante waarde het hardst?")}</h2>
          <p className="text-xs text-muted-foreground mt-1 max-w-3xl">
            {t("Één-variabele-tegelijk sweep rond het basisscenario. Rentes en groeivoeten schuiven ±1 procentpunt; bedragen ±{p}%.").replace("{p}", String(Math.round(step * 100)))}
          </p>
        </div>
        <div className="flex items-center gap-2 flex-wrap">
          <HelpButton section="sensitivity" label="Uitleg sensitivity-analyse" size="sm" />
          <span className={`text-[10px] px-2 py-1 font-bold uppercase tracking-widest ${isPremium ? "bg-primary text-primary-foreground" : "bg-muted text-muted-foreground"}`}>
            {isPremium ? (user?.tier === "TRIAL" ? "TRIAL" : "PREMIUM") : t("PREMIUM vereist")}
          </span>
          <label className="text-xs stat-label">{t("Bereik")}
            <select
              value={step}
              onChange={(e) => setStep(parseFloat(e.target.value))}
              disabled={!isPremium || loading}
              data-testid="sensitivity-step"
              className="ml-2 border-2 border-foreground bg-background px-2 py-1 font-mono-num text-xs">
              <option value={0.1}>±10%</option>
              <option value={0.2}>±20%</option>
              <option value={0.3}>±30%</option>
              <option value={0.5}>±50%</option>
            </select>
          </label>
          <button
            onClick={run}
            disabled={!isPremium || loading}
            data-testid="sensitivity-run"
            className={`btn-brutal px-4 py-2 font-display uppercase font-bold text-sm inline-flex items-center gap-2 ${isPremium ? "bg-primary text-primary-foreground" : "bg-muted text-muted-foreground cursor-not-allowed"}`}
          >
            <Sparkles className="w-4 h-4" /> {loading ? t("Bezig…") : t("Bereken")}
          </button>
        </div>
      </header>

      {!isPremium && (
        <div className="p-6 flex items-center gap-3 border-b-2 border-foreground/10 bg-muted/40" data-testid="sensitivity-lock-banner">
          <Lock className="w-5 h-5" />
          <div className="text-sm">
            <div className="font-display font-black uppercase tracking-tight">{t("Gevoeligheidsanalyse is een PREMIUM-functie")}</div>
            <div className="text-muted-foreground">{t("Start een 14-daagse proef of upgrade om te zien welke aannames de netto contante waarde het hardst raken.")}</div>
          </div>
        </div>
      )}

      {isPremium && !result && (
        <div className="p-6 text-sm text-muted-foreground" data-testid="sensitivity-empty">
          {t("Klik op 'Bereken' om de tornado-analyse te draaien.")}
        </div>
      )}

      {isPremium && result && (
        <div className="p-5">
          <div className="mb-4 grid sm:grid-cols-3 gap-4">
            <Kpi label={t("Basis-NPV")} value={fmtCurrency(result.baseline_npv)} />
            <Kpi label={t("Aantal variabelen")} value={String((result.entries || []).length)} />
            <Kpi label={t("Bereik")} value={`±${Math.round(step * 100)}%`} />
          </div>
          <div className="h-[420px]" data-testid="sensitivity-chart">
            <ResponsiveContainer width="100%" height="100%">
              <BarChart
                data={(result.entries || []).map((e) => ({
                  label: labelFor(e.label),
                  down: e.delta_low ?? 0,
                  up: e.delta_high ?? 0,
                }))}
                layout="vertical"
                stackOffset="sign"
                margin={{ top: 10, right: 30, left: 40, bottom: 10 }}
              >
                <CartesianGrid stroke={gridColor} />
                <XAxis type="number" tickFormatter={(v) => fmtCurrency(v)} tick={{ fill: axisColor }} />
                <YAxis type="category" dataKey="label" width={210} tick={{ fill: axisColor, fontSize: 11 }} />
                <Tooltip
                  contentStyle={{
                    background: "hsl(var(--card))",
                    border: "2px solid hsl(var(--foreground))",
                    color: "hsl(var(--foreground))",
                  }}
                  itemStyle={{ color: "hsl(var(--foreground))" }}
                  formatter={(v) => fmtCurrency(v)}
                />
                <ReferenceLine x={0} stroke={zeroColor} strokeWidth={2} />
                <Bar dataKey="down" stackId="s" name={t("Ondergrens")}>
                  {(result.entries || []).map((e, i) => (
                    <Cell key={`d-${i}`} fill={(e.delta_low ?? 0) >= 0 ? "#0047FF" : "#FF5900"} />
                  ))}
                </Bar>
                <Bar dataKey="up" stackId="s" name={t("Bovengrens")}>
                  {(result.entries || []).map((e, i) => (
                    <Cell key={`u-${i}`} fill={(e.delta_high ?? 0) >= 0 ? "#0047FF" : "#FF5900"} />
                  ))}
                </Bar>
              </BarChart>
            </ResponsiveContainer>
          </div>
          <table className="w-full text-sm mt-4" data-testid="sensitivity-table">
            <thead>
              <tr className="text-left border-b-2 border-foreground">
                <th className="stat-label py-1 pr-3">{t("Variabele")}</th>
                <th className="stat-label py-1 pr-3 text-right">{t("ΔNPV ondergrens")}</th>
                <th className="stat-label py-1 pr-3 text-right">{t("ΔNPV bovengrens")}</th>
                <th className="stat-label py-1 text-right">{t("Max |Δ|")}</th>
              </tr>
            </thead>
            <tbody>
              {(result.entries || []).map((e, i) => {
                const maxAbs = Math.max(Math.abs(e.delta_low || 0), Math.abs(e.delta_high || 0));
                const dl = e.delta_low || 0;
                const dh = e.delta_high || 0;
                return (
                  <tr key={i} className={i % 2 ? "bg-muted/40" : ""}>
                    <td className="py-1 pr-3">{labelFor(e.label)}</td>
                    <td className={`py-1 pr-3 font-mono-num text-right ${dl >= 0 ? "text-primary" : "text-destructive"}`}>{fmtInt(dl)}</td>
                    <td className={`py-1 pr-3 font-mono-num text-right ${dh >= 0 ? "text-primary" : "text-destructive"}`}>{fmtInt(dh)}</td>
                    <td className="py-1 font-mono-num text-right font-bold">{fmtInt(maxAbs)}</td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}

function Kpi({ label, value }) {
  return (
    <div className="border-2 border-foreground p-3">
      <div className="stat-label">{label}</div>
      <div className="font-display font-black text-2xl">{value}</div>
    </div>
  );
}
